import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthLayout from "@/ui/components/auth/AuthLayout";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { useToast } from "@/controllers/hooks/use-toast";
import { Loader2, Shield, ArrowLeft, AlertTriangle } from "lucide-react";
import { supabase } from "@/infra/supabase/client";
import { irParaOsAgentes } from "@/config/agentes";

type LinkState = "checking" | "ready" | "invalid";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [linkState, setLinkState] = useState<LinkState>("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // token_hash da query string: NAO e verificado no load (imune a prefetch
  // de scanner de email). So consumimos o token quando o usuario envia a
  // nova senha. Caso o template antigo ainda use ConfirmationURL, caimos no
  // fallback de sessao recovery via hash.
  const tokenHash = searchParams.get("token_hash");
  const type = searchParams.get("type");

  useEffect(() => {
    // Erro vindo do endpoint /verify do Supabase (ex: link velho/ja usado)
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    if (hash.get("error")) {
      setLinkState("invalid");
      return;
    }

    if (tokenHash && (type === "recovery" || type === null)) {
      setLinkState("ready");
      return;
    }

    // Fallback: template padrao (ConfirmationURL) entrega sessao via hash e
    // dispara PASSWORD_RECOVERY. Verificamos se ha sessao de recovery ativa.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setLinkState("ready");
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setLinkState("ready");
      else setLinkState((prev) => (prev === "ready" ? prev : "invalid"));
    });

    return () => sub.subscription.unsubscribe();
  }, [tokenHash, type]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 8) {
      toast({
        title: "Senha muito curta",
        description: "A nova senha deve ter pelo menos 8 caracteres.",
        variant: "destructive",
      });
      return;
    }
    if (password !== confirm) {
      toast({
        title: "Senhas não coincidem",
        description: "A nova senha e a confirmação devem ser iguais.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Consome o token de uso unico SO agora (no clique), nunca no load.
      if (tokenHash) {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          type: "recovery",
          token_hash: tokenHash,
        });
        if (verifyError) {
          setLinkState("invalid");
          return;
        }
      }

      const { data: atualizado, error } = await supabase.auth.updateUser({
        password,
      });
      if (error) throw error;

      // O app é só do admin: o aluno volta pra página dos agentes, que avisa
      // pra voltar à janela de conexão se ele estava no meio dela.
      const { data: perfil } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("id", atualizado.user.id)
        .maybeSingle();
      if (!perfil?.is_admin) {
        await irParaOsAgentes("senha");
        return;
      }

      toast({
        title: "Senha redefinida!",
        description: "Você já pode entrar com a nova senha.",
        className: "toast-success",
      });
      await supabase.auth.signOut();
      navigate("/login", { replace: true });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Tente novamente em instantes.";
      toast({
        title: "Erro ao redefinir senha",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (linkState === "checking") {
    return (
      <AuthLayout title="Redefinir senha" subtitle="Validando seu link...">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-accent-800" />
        </div>
      </AuthLayout>
    );
  }

  if (linkState === "invalid") {
    return (
      <AuthLayout
        title="Link inválido ou expirado"
        subtitle="Não foi possível validar seu link de recuperação"
      >
        <div className="space-y-6 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-foreground-secondary">
            O link pode ter expirado ou já ter sido usado. Solicite um novo link
            de recuperação para continuar.
          </p>
          <div className="space-y-3">
            <Button
              type="button"
              variant="premium"
              size="lg"
              className="w-full"
              onClick={() => navigate("/recuperar")}
            >
              Solicitar novo link
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="lg"
              className="w-full"
              onClick={() => navigate("/login")}
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Login
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Redefinir senha"
      subtitle="Escolha uma nova senha para sua conta"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label
            htmlFor="new-password"
            className="text-foreground-secondary font-medium"
          >
            Nova senha
          </Label>
          <Input
            id="new-password"
            type="password"
            placeholder="Mínimo de 8 caracteres"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            className="h-12 bg-white/60 border-black/[0.08] text-neutral-900 placeholder:text-neutral-400 rounded-xl transition-all hover:border-black/[0.12] focus:border-accent-800/50 focus:ring-2 focus:ring-accent-800/10"
          />
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="confirm-password"
            className="text-foreground-secondary font-medium"
          >
            Confirmar nova senha
          </Label>
          <Input
            id="confirm-password"
            type="password"
            placeholder="Repita a nova senha"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            className="h-12 bg-white/60 border-black/[0.08] text-neutral-900 placeholder:text-neutral-400 rounded-xl transition-all hover:border-black/[0.12] focus:border-accent-800/50 focus:ring-2 focus:ring-accent-800/10"
          />
        </div>

        <Button
          type="submit"
          variant="premium"
          size="lg"
          className="w-full"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Salvando...
            </>
          ) : (
            "Redefinir senha"
          )}
        </Button>
      </form>

      <div className="bg-accent-50 border border-accent-200 rounded-lg p-4 hover-glow mt-6">
        <div className="flex items-center space-x-2 text-accent-800 mb-2">
          <Shield className="w-4 h-4" />
          <span className="text-sm font-medium">Processo Seguro</span>
        </div>
        <p className="text-xs text-foreground-secondary">
          Sua nova senha é criptografada. Após redefinir, use-a para entrar na
          sua conta.
        </p>
      </div>
    </AuthLayout>
  );
}
